import { Box, ButtonGroup, Drawer, Grid, IconButton, Tooltip, Zoom } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Create, ThumbDownAlt, ThumbUpAlt } from "@material-ui/icons";
import { useAccessControl, useAuth, useVerifyPermission } from "@airborne/react-admin";
import * as React from "react";
import { FC, Fragment } from "react";
import { Datagrid, ListProps, TextField, useNotify, useRefresh } from "react-admin";
import { DetailButton } from "../../../Components/DetailButton";
import { ConfirmButton } from "../../../Components/ConfirmButton";
import { CustomDateField } from "../../../Components/CustomDateField";
import FilterToolbar from "../../../Components/FiltersToolbar/FilterToolbar";
import { RDDDatetimeField } from "../../../Components/RDDDatetimeField";
import RequestStatusField from "../../../Components/ReferenceFields/RequestStatus/RequestStatusField";
import { DefaultCreatedDateSort, DefaultPerPageSize } from "../../../Constants/Constant";
import { useAppDataProvider } from "../../../dataprovider";
import { FilterInputType } from "../../../Enums/FilterTypes";
import { RequestStatus } from "../../../Enums/RequestStatus";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { SourceRequestOrder, WoWaitingListTitle } from "./Constant/Source";
import RequestOrderNotes from "./RequestOrderNotes";
import { RemarkTypes } from "../../../../src/Enums/RemarkTypes";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: "20%",
  },
}));
const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const { remarkAddClick, ...rest } = props;
  const dataProvider = useAppDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { user } = useAuth();
  const { canCreate, canEdit, canRead, hasAccess } = useAccessControl(SourceRequestOrder);

  const acceptButtonClick = record => e => {
    record.requestStatus = RequestStatus.CapabilityWarrantyPassed;
    dataProvider.updateRequestOrder(record).then(result => {
      notify(`Record updated`, "success", false, true);
      refresh();
    });
  };

  const rejectButtonClick = record => e => {
    record.requestStatus = RequestStatus.CapabilityWarrantyFailed;
    dataProvider.updateRequestOrder(record).then(result => {
      notify(`Record updated`, "success", false, true);
      refresh();
    });
  };

  const remarkOnClick = record => e => {
    e.stopPropagation();
    remarkAddClick?.(record);
  };

  const AcceptCoverageButton = ({ record }) => {
    return (
      <ConfirmButton
        {...props}
        variant="text"
        style={{
          color: "#12824C",
        }}
        size="medium"
        click={acceptButtonClick(record)}
      >
        <Tooltip TransitionComponent={Zoom} title="Accept">
          <ThumbUpAlt />
        </Tooltip>
      </ConfirmButton>
    );
  };

  const RejectCoverageButton = ({ record }) => {
    return (
      <ConfirmButton
        {...props}
        variant="text"
        style={{
          color: "#primary",
        }}
        size="medium"
        click={rejectButtonClick(record)}
      >
        <Tooltip TransitionComponent={Zoom} title="Reject">
          <ThumbDownAlt />
        </Tooltip>
      </ConfirmButton>
    );
  };

  const RemarkButton = ({ record }) => {
    return (
      <IconButton style={{ width: 40 }} onClick={remarkOnClick(record)}>
        <Tooltip TransitionComponent={Zoom} title="Core Last Update">
          <Create />
        </Tooltip>
      </IconButton>
    );
  };
  const DetailsButton = ({ record }) => {
    return <DetailButton record={record} resource={"requestorders"} />;
  };
  const OptButtonGroup = ({ record }) => {
    return (
      <Box style={{ flex: 1, flexDirection: "row" }}>
        <ButtonGroup variant="text" size="small">
          <DetailsButton record={record} />
          {user?.isCustomer === false && canCreate && (
            <>
              <AcceptCoverageButton record={record} />
              <RejectCoverageButton record={record} />
              <RemarkButton record={record} />{" "}
            </>
          )}
        </ButtonGroup>
      </Box>
    );
  };

  return (
    <Fragment>
      <Datagrid {...rest} optimized basePath={SourceRequestOrder}>
        <OptButtonGroup record={props} />
        <RequestStatusField label="Status" />
        <TextField source="refNo" label="Ref No" />
        <TextField source="customerPo" label="Customer Po" />
        <RDDDatetimeField label="Request Delivery Date" />
        <TextField source="customerRequestComponent" label="Pn" />
        <TextField source="unserviceable.sn" label="Core Unit Sn" />
        <CustomDateField source="unserviceable.updatedDate" label="Wo Waiting Date" showTime />
        <TextField source="unserviceable.remark" label="Core Unit Remark" />
        <TextField source="currentPbhNote" label="Current PBH Note" />
      </Datagrid>
    </Fragment>
  );
};

const WoWaitingList: FC<ListProps> = props => {
  useVerifyPermission("customroutes", "wowaitinglist.read");
  const classes = useStyles();
  const [isMatchCreate, setIsMatchCreate] = React.useState(false);
  const [requestRemark, setRequestRemark] = React.useState<any>({});
  const ExportToExcel = data => {
    ExcelExport(
      [
        {
          header: "Status",
          key: "Status",
        },
        {
          header: "Ref No",
          key: "Ref No",
        },
        {
          header: "Customer Po",
          key: "Customer Po",
        },
        {
          header: "Requested Delivery Date",
          key: "Requested Delivery Date",
        },
        {
          header: "Pn",
          key: "Pn",
        },
        {
          header: "Core Unit Sn",
          key: "Core Unit Sn",
        },
        {
          header: "Wo Waiting Date",
          key: "Wo Waiting Date",
        },
        {
          header: "Remark",
          key: "Remark",
        },
      ],
      data.map(v => [
        v.requestStatus,
        v.refNo,
        v.customerPo,
        v.requestedDeliveryDate,
        v.customerRequestComponent,
        v.unserviceable?.sn,
        v.unserviceable?.updatedDate,
        v.unserviceable?.remark,
      ]),
      "Wo Waiting List"
    );
  };
  const remarkAddClick = record => {
    setIsMatchCreate(true);
    setRequestRemark(record);
  };
  const handleClose = () => {
    setIsMatchCreate(false);
  };
  return (
    <Grid container>
      <Grid item xs={isMatchCreate ? 9 : 12}>
        <PoolList
          {...props}
          hasCreate={false}
          exporter={ExportToExcel}
          bulkActionButtons={false}
          resource={SourceRequestOrder}
          basePath={SourceRequestOrder}
          title={WoWaitingListTitle}
          filterDefaultValues={{
            requestStatus__eq: RequestStatus.InitialProcess,
            isUnsvcInfo__eq: true,
          }}
          sort={DefaultCreatedDateSort}
          perPage={DefaultPerPageSize}
          filters={
            <FilterToolbar
              dataSource={[
                {
                  source: "requestStatus",
                  inputType: FilterInputType.Select,
                  label: "Status",
                  optionText: "label",
                  optionValue: "id",
                  style: { width: "250px" },
                  choices: [
                    {
                      label: "Initial Process",
                      id: RequestStatus.InitialProcess,
                    },
                  ],
                },
                {
                  source: "refNo",
                  inputType: FilterInputType.Text,
                  label: "Ref No",
                },
                {
                  source: "customerPo",
                  inputType: FilterInputType.Text,
                  label: "Customer Po",
                },
                {
                  source: "requestedDeliveryDate",
                  inputType: FilterInputType.Date,
                  label: "Requested Delivery Date",
                },
                {
                  source: "customerRequestComponent",
                  inputType: FilterInputType.Text,
                  label: "Pn",
                },
                {
                  source: "unserviceable.sn",
                  inputType: FilterInputType.Text,
                  label: "Sn",
                },
                {
                  source: "unserviceable.remark",
                  inputType: FilterInputType.Text,
                  label: "Remark",
                },
              ]}
            />
          }
        >
          <TabbedDatagrid remarkAddClick={remarkAddClick} />
        </PoolList>
      </Grid>
      {isMatchCreate && (
        <Grid item xs={3}>
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant="persistent"
            open={isMatchCreate}
            anchor="right"
            onClose={handleClose}
          >
            {isMatchCreate ? (
              <RequestOrderNotes
                onCancel={handleClose}
                request={requestRemark}
                resource="requestremarks"
                basePath="requestremarks"
                remarkType={RemarkTypes.PbhNote}
              />
            ) : null}
          </Drawer>
        </Grid>
      )}
    </Grid>
  );
};

export default WoWaitingList;
